import { useState, type FormEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { siteConfig } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'ok' | 'error'

// Labels PT pros campos que a API valida, pra transformar o `issues`
// (fieldErrors do zod) numa mensagem legível em vez de "Dados inválidos."
const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  company: 'Empresa',
  service: 'Serviço',
  message: 'Mensagem',
}

// Mensagem amigável por campo, a partir do `issues` (fieldErrors do zod).
function fieldMessage(field: string, msgs?: string[]): string {
  const label = FIELD_LABELS[field] || field
  if (field === 'message') return 'Escreva pelo menos 10 caracteres.'
  if (field === 'name') return 'Informe pelo menos 2 caracteres.'
  if (field === 'email') return 'Informe um e-mail válido.'
  return msgs?.[0] ?? `${label} inválido.`
}

function issuesToFieldErrors(
  issues: Record<string, string[] | undefined>
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [field, msgs] of Object.entries(issues)) {
    if (msgs && msgs.length > 0) out[field] = fieldMessage(field, msgs)
  }
  return out
}

export function Contato() {
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Limpa o erro de um campo assim que o usuário começa a corrigi-lo.
  function clearFieldError(name: string) {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('sending')
    setErrorMsg('')
    setFieldErrors({})
    const form = e.currentTarget
    const fd = new FormData(form)
    const payload = {
      name: String(fd.get('name') || ''),
      email: String(fd.get('email') || ''),
      company: String(fd.get('company') || ''),
      service: String(fd.get('service') || ''),
      message: String(fd.get('message') || ''),
      botcheck: String(fd.get('botcheck') || ''),
    }

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok && json.success) {
        setStatus('ok')
        form.reset()
      } else {
        setStatus('error')
        // A API devolve `issues` (fieldErrors do zod) dizendo qual campo falhou.
        // Marcamos os campos inline e mostramos um resumo curto no topo.
        if (json.issues && typeof json.issues === 'object') {
          const fe = issuesToFieldErrors(json.issues)
          setFieldErrors(fe)
          const first = Object.keys(fe)[0]
          const el = first ? form.elements.namedItem(first) : null
          if (el instanceof HTMLElement) el.focus()
          setErrorMsg('Confira os campos destacados abaixo.')
        } else {
          setErrorMsg(json.message || 'Falha ao enviar. Tenta de novo daqui a pouco.')
        }
      }
    } catch (err) {
      setStatus('error')
      setErrorMsg('Sem conexão. Tenta de novo ou manda direto pro email abaixo.')
    }
  }

  // Classe extra pra pintar a borda de vermelho quando o campo tem erro.
  const errBorder = (name: string) =>
    fieldErrors[name] ? 'border-destructive focus-visible:ring-destructive' : ''

  // Mensagem de erro inline abaixo do campo.
  const FieldError = ({ name }: { name: string }) =>
    fieldErrors[name] ? (
      <p id={`${name}-error`} className="text-xs text-destructive">
        {fieldErrors[name]}
      </p>
    ) : null

  return (
    <>
      <Helmet>
        <title>Contato — i3 Solutions</title>
        <meta
          name="description"
          content="Fale com a i3 Solutions. Orçamentos, projetos, parcerias. Resposta em 1 dia útil."
        />
        <link rel="canonical" href="https://i3solutions.tech/contato" />
      </Helmet>

      <section className="border-b border-border/30">
        <div className="container py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold tracking-wider text-primary uppercase mb-3">
              Contato
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-balance">
              Vamos conversar sobre o seu projeto
            </h1>
            <p className="mt-6 text-lg text-muted-foreground text-pretty">
              Conta o problema, o contexto e o resultado esperado. Em 1 dia útil a gente volta com
              direção concreta — não com proposta inflada.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 space-y-6"
          >
            <a
              href={`mailto:${siteConfig.email}`}
              className="flex items-start gap-4 p-5 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-colors"
            >
              <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
                <div className="font-semibold">{siteConfig.email}</div>
              </div>
            </a>

            <a
              href={siteConfig.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-5 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-colors"
            >
              <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Phone className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</div>
                <div className="font-semibold">{siteConfig.phone}</div>
              </div>
            </a>

            <div className="flex items-start gap-4 p-5 rounded-xl border border-border/50 bg-card">
              <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Endereço</div>
                <div className="font-semibold">
                  {siteConfig.address.street}, {siteConfig.address.neighborhood}
                </div>
                <div className="text-sm text-muted-foreground">
                  {siteConfig.address.city}/{siteConfig.address.state}, {siteConfig.address.country}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="lg:col-span-3 p-6 md:p-8 rounded-2xl border border-border/50 bg-card space-y-5"
          >
            {/* honeypot anti-spam */}
            <input
              type="checkbox"
              name="botcheck"
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
            />

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  minLength={2}
                  placeholder="Seu nome"
                  className={errBorder('name')}
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                  onChange={() => clearFieldError('name')}
                />
                <FieldError name="name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="voce@empresa.com"
                  className={errBorder('email')}
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  onChange={() => clearFieldError('email')}
                />
                <FieldError name="email" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="company">Empresa</Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  placeholder="Nome da empresa"
                  className={errBorder('company')}
                  aria-invalid={!!fieldErrors.company}
                  aria-describedby={fieldErrors.company ? 'company-error' : undefined}
                  onChange={() => clearFieldError('company')}
                />
                <FieldError name="company" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service">Serviço de interesse</Label>
                <select
                  id="service"
                  name="service"
                  aria-invalid={!!fieldErrors.service}
                  aria-describedby={fieldErrors.service ? 'service-error' : undefined}
                  onChange={() => clearFieldError('service')}
                  className={`flex h-11 w-full rounded-md border bg-background/50 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    fieldErrors.service
                      ? 'border-destructive focus-visible:ring-destructive'
                      : 'border-input'
                  }`}
                >
                  <option value="">Selecionar...</option>
                  <option>Arquitetura de Software</option>
                  <option>Squad as a Service</option>
                  <option>Infraestrutura / DevOps</option>
                  <option>Desenvolvimento Web</option>
                  <option>Desenvolvimento Mobile</option>
                  <option>Cyber Security</option>
                  <option>Data Analytics</option>
                  <option>Outro</option>
                </select>
                <FieldError name="service" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Mensagem *</Label>
              <Textarea
                id="message"
                name="message"
                required
                minLength={10}
                rows={5}
                placeholder="Conta o problema, contexto, prazo desejado..."
                className={errBorder('message')}
                aria-invalid={!!fieldErrors.message}
                aria-describedby={fieldErrors.message ? 'message-error' : 'message-hint'}
                onChange={() => clearFieldError('message')}
              />
              {fieldErrors.message ? (
                <FieldError name="message" />
              ) : (
                <p id="message-hint" className="text-xs text-muted-foreground">
                  Mínimo de 10 caracteres.
                </p>
              )}
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={status === 'sending'}>
              {status === 'sending' ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Enviando...
                </>
              ) : (
                'Enviar mensagem'
              )}
            </Button>

            {status === 'ok' && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-primary/10 border border-primary/30 text-sm">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-primary">Mensagem enviada!</div>
                  <div className="text-muted-foreground">Voltamos em 1 dia útil.</div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-destructive/10 border border-destructive/30 text-sm">
                <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-destructive">Falhou.</div>
                  <div className="text-muted-foreground">{errorMsg}</div>
                </div>
              </div>
            )}
          </motion.form>
        </div>
      </section>
    </>
  )
}
